<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Navbar />
    <div class="conversation-page">
      <el-card shadow="always" class="conversation-card">
        <template #header>
          <div class="conversation-header">
            <el-button link @click="$router.push('/Messages')">← Messages</el-button>
            <h2 v-if="otherUser">
              <el-avatar :size="32" :src="getProfilePictureUrl(otherUser.profile_picture)" />
              <UserLink :username="otherUser.username" />
            </h2>
          </div>
        </template>

        <el-empty v-if="notFound" description="Cet utilisateur n'existe pas ou plus." />

        <!-- v-show (et non v-else) : element-plus plante si le textarea autosize est démonté juste après son montage -->
        <div v-show="!notFound" class="chat-wrapper">
          <el-scrollbar ref="scrollbar" class="messages-scroll">
            <p v-if="loaded && messages.length === 0" class="empty-thread">
              Aucun message pour le moment. Écrivez le premier !
            </p>
            <div
              v-for="msg in messages"
              :key="msg.id"
              :class="['message-row', { own: isMine(msg) }]"
            >
              <div class="message-bubble">
                <div class="message-content">{{ msg.content }}</div>
                <div class="message-time">{{ formatDate(msg.createdAt) }}</div>
              </div>
              <el-button
                v-if="isMine(msg)"
                type="danger"
                link
                size="small"
                @click="deleteMessage(msg.id)"
              >
                <el-icon><Delete /></el-icon>
              </el-button>
            </div>
          </el-scrollbar>
          <div class="chat-input-row">
            <!-- Entrée va à la ligne ; envoi par le bouton ou Ctrl+Entrée (⌘+Entrée sur Mac) -->
            <el-input
              v-model="newMessage"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 5 }"
              placeholder="Écrire un message... (Ctrl+Entrée pour envoyer)"
              :maxlength="maxMessageLength"
              show-word-limit
              @keydown.enter.ctrl.prevent="sendMessage"
              @keydown.enter.meta.prevent="sendMessage"
            />
            <el-button type="primary" class="send-btn" @click="sendMessage">
              <el-icon><Promotion /></el-icon>
            </el-button>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import UserLink from '@/components/UserLink.vue'
import api, { API_BASE_URL } from '@/api'
import { notifyError, confirmAction } from '@/notify'
import { fetchSessionUser } from '@/session'
import { Delete, Promotion } from '@element-plus/icons-vue'

// Intervalle de rafraîchissement du fil (ms), comme le chat des groupes d'entraide
const POLL_INTERVAL = 5000

export default {
  components: { Navbar, UserLink, Delete, Promotion },

  props: {
    userId: { type: [String, Number], required: true },
  },

  data() {
    return {
      sessionUser: null,
      otherUser: null,
      messages: [],
      newMessage: '',
      maxMessageLength: 2000,
      loaded: false,
      notFound: false,
      pollInterval: null,
    }
  },

  async created() {
    this.sessionUser = await fetchSessionUser()
    await this.fetchConversation()
    this.scrollToBottom()
  },

  mounted() {
    document.body.style.backgroundColor = 'rgba(21, 109, 123)'
    this.pollInterval = setInterval(this.fetchConversation, POLL_INTERVAL)
  },

  beforeUnmount() {
    clearInterval(this.pollInterval)
  },

  watch: {
    // Navigation d'une conversation à une autre sans démonter la vue
    async userId() {
      this.messages = []
      this.otherUser = null
      this.notFound = false
      await this.fetchConversation()
      this.scrollToBottom()
    },
  },

  methods: {
    isMine(msg) {
      return !!this.sessionUser && Number(msg.senderId) === Number(this.sessionUser.id)
    },

    async fetchConversation() {
      if (this.notFound) return
      try {
        const response = await api.get(`/message/conversation/${this.userId}`)
        const hadNewMessages = response.data.messages.length !== this.messages.length
        this.otherUser = response.data.user
        this.messages = response.data.messages
        if (hadNewMessages && this.loaded) this.scrollToBottom()
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.notFound = true
          clearInterval(this.pollInterval)
        }
      } finally {
        this.loaded = true
      }
    },

    async sendMessage() {
      const content = this.newMessage.trim()
      if (!content) return
      try {
        await api.post('/message/send', { receiverId: Number(this.userId), content })
        this.newMessage = ''
        await this.fetchConversation()
        this.scrollToBottom()
      } catch (error) {
        notifyError(error, "Le message n'a pas pu être envoyé")
      }
    },

    async deleteMessage(messageId) {
      if (!(await confirmAction('Supprimer ce message ?'))) return
      try {
        await api.delete(`/message/delete/${messageId}`)
        this.messages = this.messages.filter((m) => m.id !== messageId)
      } catch (error) {
        notifyError(error, 'Erreur lors de la suppression du message')
      }
    },

    async scrollToBottom() {
      await this.$nextTick()
      const scrollbar = this.$refs.scrollbar
      if (scrollbar && scrollbar.setScrollTop) scrollbar.setScrollTop(1e9)
    },

    getProfilePictureUrl(path) {
      return `${API_BASE_URL}/${path}`
    },

    formatDate(dateString) {
      const options = { day: 'numeric', month: 'short', hour: 'numeric', minute: 'numeric' }
      return new Date(dateString).toLocaleDateString('fr-FR', options)
    },
  },
}
</script>

<style scoped>
.conversation-page {
  padding: 70px 20px 20px;
  max-width: 700px;
  margin: 0 auto;
}

.conversation-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.conversation-header h2 {
  margin: 0;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
}

.chat-wrapper {
  display: flex;
  flex-direction: column;
  height: 60vh;
  min-height: 320px;
}

.messages-scroll {
  flex: 1;
  background: #f5f7fa;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.empty-thread {
  color: #909399;
  text-align: center;
}

.message-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.message-row.own {
  flex-direction: row-reverse;
}

.message-bubble {
  background: white;
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 70%;
  word-break: break-word;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.message-row.own .message-bubble {
  background: #ecf5ff;
}

.message-content {
  color: #303133;
  font-size: 14px;
  white-space: pre-wrap;
}

.message-time {
  font-size: 11px;
  color: #909399;
  margin-top: 4px;
  text-align: right;
}

.chat-input-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
</style>
